import { axiosInstance } from "../lib/axios.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { supabase } from "../lib/supabaseClient";

export const userApi = {
  // Get user preferences
  async getPreferences() {
    try {
      const response = await axiosInstance.get('/users/preferences');
      return response.data;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  },

  // Update user preferences
  async updatePreferences(preferences) {
    try {
      const response = await axiosInstance.patch('/users/preferences', preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  }
};

// ---------------- React Query Hooks ----------------

/**
 * Hook: useUpdateUserProfile
 * PUT /auth/profile with JSON or multipart (if avatar File/Blob).
 */
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      // Always use FormData now
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      
      const { data } = await axiosInstance.put("/auth/profile", formData, {
        headers: {
          // Override default header set in axiosInstance for FormData request
          "Content-Type": "multipart/form-data",
        },
      });
      return data;
    },
    onSuccess: async (data) => {
      // If backend indicates password change, force re-auth flow
      if (data?.reauthRequired) {
        toast.success('Password updated. Please log in again.');
        try {
          await supabase.auth.signOut();
        } catch (_) {}
        try {
          Object.keys(localStorage)
            .filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
            .forEach(k => localStorage.removeItem(k));
        } catch (_) {}
        localStorage.removeItem('user');
        window.location.href = '/login';
        return; // Do not proceed with normal success flow
      }

      toast.success(data?.message || "Profile updated successfully");
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
      if (data?.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || "Failed to update profile");
    },
  });
};
